import { camelCase, difference, isEmpty, isNil, keys, mapKeys } from 'lodash';
import { DateTime } from 'luxon';
import { FEATURE_FLAGS, FeatureFlag } from '../models/models';
import * as SentryAngular from '@sentry/angular';

export type Serializer<T> = (response: unknown) => T;
export type ObjectSerializer<T> = { [P in keyof T]: Serializer<T[P]> };


export class SerializerError extends Error {
  private readonly wrapped?: SerializerError;
  private readonly fieldName?: string;
  private readonly value: string;
  private readonly type: string;
  private readonly expectedType: string;

  constructor(message: string, expectedType: string, type: string, value: string, fieldName?: string, wrapped?: SerializerError) {
    super(message);
    this.name = 'SerializerError';
    this.expectedType = expectedType;
    this.type = type;
    this.value = value;
    this.fieldName = fieldName;
    this.wrapped = wrapped;
  }

  getFieldName(): string {
    const fieldName = this.fieldName ?? '';
    const wrappedFieldName = this.wrapped?.getFieldName?.() ?? '';

    if (wrappedFieldName) {
      return `${fieldName}.${wrappedFieldName}`;
    }
    else {
      return fieldName;
    }
  }

  getFieldValue(): string {
    return this.wrapped ? this.wrapped.getFieldValue() : this.value;
  }

  getFieldType(): string {
    return this.wrapped ? this.wrapped.getFieldType() : this.type;
  }

  getExpectedType(): string {
    return this.wrapped ? this.wrapped.getExpectedType() : this.expectedType;
  }

}


export function serializeArray<T>(serializer: Serializer<T>): Serializer<T[]> {
  return response => {
    if (!Array.isArray(response)) {
      throw new SerializerError(
        'Expected to receive array in response',
        'array',
        `${typeof response}`,
        `${response}`,
      );
    }

    return response.map(serializer);
  };
}

export function serializeObjectKeysToSnakeCase<T>(serializer: Serializer<T>): Serializer<T> {
  return (response) => {
    if (typeof response !== 'object' || response === null) {
      throw new SerializerError(
        'Only \'object\' type is supported for DTO key conversion.',
        'object',
        `${typeof response}`,
        `${response}`,
      );
    }

    const keyConverted = mapKeys(response, (_value, key) => camelCase(key));

    return serializer(keyConverted);
  };
}

function checkFieldPresence<T>(object: T, definition: ObjectSerializer<T>): void {
  const presentKeys = keys(object);
  const definedKeys = keys(definition);

  const SEPARATOR = ', ';
  const unknownKeys = difference(presentKeys, definedKeys);

  if (!isEmpty(unknownKeys)) {
    console.warn(`The following keys do not have a serializer defined: ${unknownKeys.join(SEPARATOR)}`, object);
  }
}

export function serializeObject<T>(serializer: ObjectSerializer<T>): Serializer<T> {
  return (response: any) => {
    checkFieldPresence(response, serializer);

    const result = {} as T;
    let value;
    for (const key of Object.keys(serializer)) {
      value = response[key as keyof T];
      try {
        // Keys are already checked
        result[key as keyof T] = serializer[key as keyof T](value);
      } catch (error: any) {
        throw new SerializerError(
          `${error.message}`,
          'object',
          `${typeof value}`,
          `${value}`,
          `${key}`,
          error,
        );
      }
    }
    return result;
  };
}

export const serializeText: Serializer<string> = response => {
  if (typeof response !== 'string') {
    throw new SerializerError(
      'Expected to receive string type in response.',
      'string',
      `${typeof response}`,
      `${response}`,
    );
  }
  return response;
};

export const serializeNumber: Serializer<number> = response => {
  if (typeof response !== 'number') {
    throw new SerializerError(
      'Expected to receive number type in response.',
      'number',
      `${typeof response}`,
      `${response}`,
    );
  }
  return response;
};

export const serializeBoolean: Serializer<boolean> = response => {
  if (typeof response !== 'boolean') {
    throw new SerializerError(
      'Expected to receive boolean type in response.',
      'boolean',
      `${typeof response}`,
      `${response}`,
    );
  }
  return response;
};

export function serializeWithDefault<T, S>(serializer: Serializer<T>, defaultValue: S): Serializer<T | S> {
  return response => {
    if (isNil(response)) {
      return defaultValue;
    }

    return serializer(response);
  };
}

export function serializeNullable<T>(serializer: Serializer<T>): Serializer<T | null> {
  return response => {
    if (response === null) {
      return null;
    }

    return serializer(response);
  };
}

export function serializeOptional<T>(serializer: Serializer<T>): Serializer<T | undefined> {
  return response => {
    if (typeof response === 'undefined') {
      return undefined;
    }

    return serializer(response);
  };
}

export function serializeWithFirstMatching<T1, T2>(...serializers: [Serializer<T1>, Serializer<T2>]): Serializer<T1 | T2> {
  return response => {
    const errors: string[] = [];

    for (const serializer of serializers) {
      try {
        return serializer(response);
      } catch (error: any) {
        errors.push(error.message);
      }
    }

    throw new SerializerError(
      `Error while serializing combined field. Errors: ${errors.join(', ')}`,
      'combined field',
      `${typeof response}`,
      `${response}`,
    );
  };
}

export const serializeUnknown: Serializer<unknown> = response => response;

export const serializeIdentical = <T>(response: unknown) => response as T;

export function serializeGenericObject<T extends object = object>(response: unknown): T {
  if (typeof response !== 'object' || response === null) {
    throw new SerializerError(
      `Expected to receive non null object.`,
      'not null object',
      `${typeof response}`,
      `${response}`,
    );
  }

  return response as T;
}

export const serializeDateTime: Serializer<DateTime> = response => {
  if (typeof response !== 'string') {
    throw new SerializerError(
      'Expected to receive string type date in response.',
      'string',
      `${typeof response}`,
      `${response}`,
    );
  }
  const convertedDate = DateTime.fromISO(response);

  if (!convertedDate.isValid) {
    throw new SerializerError(
      `Could not convert string to DateTime. Reason: ${convertedDate.invalidExplanation}`,
      'string',
      `${typeof response}`,
      `${response}`,
    );
  }

  return convertedDate;
};

export const serializeFeatureFlag: Serializer<FeatureFlag> = response => {
  if (!Object.values(FEATURE_FLAGS).includes(response as FeatureFlag)) {
    SentryAngular.captureException(new Error(`Unrecognized feature flag received: ${response}`));
  }

  return response as FeatureFlag;
};
